import { useMemo } from "react";
import { useHotelGuestCount } from "@apac/hooks";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import {
  type RoomProduct,
  CancellationPolicyV2Enum,
} from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";

export const useRoomProductDetails = ({
  total,
  mealPlan,
  cancellationPolicyV2,
  perNightTotal,
}: RoomProduct) => {
  const { t, formatFiatCurrency } = useI18nContext();

  const { kind: mealType } = mealPlan ?? {};

  const planName = useMemo(
    () => (mealType ? t(`hotelShop.mealPlan.${mealType}`) : ""),
    [mealType, t]
  );

  const { title, subtitle } = useMemo(() => {
    switch (cancellationPolicyV2?.CancellationPolicyV2) {
      case CancellationPolicyV2Enum.FullyRefundable:
        return {
          title: t("hotelShop.hotelCard.refundable"),
          subtitle: cancellationPolicyV2.primaryText,
        };
      case CancellationPolicyV2Enum.PartiallyRefundable:
        return {
          title: t("hotelShop.hotelCard.partiallyRefundable"),
          subtitle: cancellationPolicyV2.primaryText,
        };
      case CancellationPolicyV2Enum.NonRefundable:
        return {
          title: t("hotelShop.hotelCard.nonRefundable"),
          subtitle: "",
        };
      default:
        return ensureExhaustive(
          cancellationPolicyV2?.CancellationPolicyV2 as never,
          {
            title: "",
            subtitle: "",
          }
        );
    }
  }, [cancellationPolicyV2, t]);

  const totalPrice = formatFiatCurrency(total.fiat);
  const pricePerNight = formatFiatCurrency(perNightTotal.fiat);
  const {
    counts: { totalCount },
  } = useHotelGuestCount();

  return {
    title,
    subtitle,
    planName,
    totalPrice,
    pricePerNight,
    guest: totalCount,
  };
};
